import React, { useEffect, useState } from "react";
import styled from "styled-components";
import {
    LottieLoadingIndicator,
    StandardModal,
    StandardModalActionButton,
    StandardModalActionGroup,
    StandardModalErrorText,
    StandardModalInput,
    StandardModalSelect,
    StandardModalTable,
    StandardModalTableScroll,
    StandardModalTableSection,
    portalColors
} from "src/components";
import {
    DirectoryCsvRow,
    DirectoryCustomFieldDefinition,
    DirectoryVisibility,
    validateDirectoryCsvRows
} from "../directoryCsv";

interface DirectoryCsvPreviewModalProps {
    open: boolean;
    rows: DirectoryCsvRow[];
    customFields: DirectoryCustomFieldDefinition[];
    employeeLabel: string;
    clientLabel: string;
    isSaving: boolean;
    onBack: () => void;
    onClose: () => void;
    onSave: (rows: DirectoryCsvRow[]) => void;
}

type EditableField = Exclude<
    keyof DirectoryCsvRow,
    "id" | "visibility" | "customFields"
>;

const PreviewLayout = styled.div`
    display: grid;
    gap: 1rem;
    min-width: 0;
`;

const PreviewToolbar = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 0.75rem;
`;

const ListingCount = styled.div`
    padding: 0.75rem 0.9rem;
    border: 1px solid ${portalColors.border};
    border-radius: 8px;
    background: ${portalColors.surfaceSoft};
    color: ${portalColors.text};
    font-size: 0.875rem;
    font-weight: 800;
`;

const SelectCheckbox = styled.input.attrs({ type: "checkbox" })`
    width: 17px;
    height: 17px;
    accent-color: ${portalColors.text};
    cursor: pointer;
`;

const PreviewTable = styled(StandardModalTable)`
    input,
    select {
        height: 38px;
        padding-left: 0.7rem;
        padding-right: 0.7rem;
    }

    th,
    td {
        min-width: 9rem;
        padding: 0.5rem;
    }

    th:first-child,
    td:first-child {
        min-width: 2.75rem;
        width: 2.75rem;
        text-align: center;
    }
`;

const DirectoryCsvPreviewModal = ({
    open,
    rows,
    customFields,
    employeeLabel,
    clientLabel,
    isSaving,
    onBack,
    onClose,
    onSave
}: DirectoryCsvPreviewModalProps): React.JSX.Element => {
    const [editableRows, setEditableRows] = useState<DirectoryCsvRow[]>(rows);
    const [selectedIds, setSelectedIds] = useState<string[]>([]);
    const [errors, setErrors] = useState<string[]>([]);

    useEffect(() => {
        setEditableRows(rows);
        setSelectedIds([]);
        setErrors([]);
    }, [rows]);

    const updateField = (rowId: string, field: EditableField, value: string): void => {
        setEditableRows(currentRows => currentRows.map(row => (
            row.id === rowId ? { ...row, [field]: value } : row
        )));
    };

    const updateCustomField = (rowId: string, fieldUuid: string, value: string): void => {
        setEditableRows(currentRows => currentRows.map(row => (
            row.id === rowId
                ? { ...row, customFields: { ...row.customFields, [fieldUuid]: value } }
                : row
        )));
    };

    const updateVisibility = (rowId: string, value: DirectoryVisibility): void => {
        setEditableRows(currentRows => currentRows.map(row => (
            row.id === rowId ? { ...row, visibility: value } : row
        )));
    };

    const toggleSelected = (rowId: string): void => {
        setSelectedIds(currentIds => currentIds.includes(rowId)
            ? currentIds.filter(id => id !== rowId)
            : [...currentIds, rowId]);
    };

    const toggleAll = (): void => {
        setSelectedIds(selectedIds.length === editableRows.length ? [] : editableRows.map(row => row.id));
    };

    const deleteSelected = (): void => {
        setEditableRows(currentRows => currentRows.filter(row => !selectedIds.includes(row.id)));
        setSelectedIds([]);
        setErrors([]);
    };

    const handleSave = (): void => {
        const nextErrors = validateDirectoryCsvRows(editableRows);
        setErrors(nextErrors);
        if (nextErrors.length === 0) {
            onSave(editableRows);
        }
    };

    const input = (row: DirectoryCsvRow, field: EditableField, label: string): React.JSX.Element => (
        <StandardModalInput
            aria-label={`${label} for row`}
            value={row[field]}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => updateField(row.id, field, event.target.value)}
        />
    );

    return (
        <StandardModal
            open={open}
            onClose={onClose}
            widthMode="table"
            title="Directory CSV List"
            description="Review and edit your uploaded listings before confirming the import. Images can be added after saving."
            paperStyle={{ minHeight: "34rem" }}
            footer={(
                <StandardModalActionGroup style={{ marginLeft: "auto" }}>
                    <StandardModalActionButton width="140px" onClick={onBack} disabled={isSaving}>
                        Back
                    </StandardModalActionButton>
                    {isSaving ? (
                        <LottieLoadingIndicator width="2em" height="2em" containerHeight="2em" margin="0" />
                    ) : (
                        <StandardModalActionButton $variant="primary" width="150px" onClick={handleSave}>
                            Confirm & Save
                        </StandardModalActionButton>
                    )}
                </StandardModalActionGroup>
            )}
        >
            <PreviewLayout>
                <PreviewToolbar>
                    <StandardModalActionButton
                        $variant="danger"
                        width="145px"
                        disabled={selectedIds.length === 0}
                        onClick={deleteSelected}
                    >
                        Delete Selected
                    </StandardModalActionButton>
                    <ListingCount>{editableRows.length.toLocaleString()} listings in CSV</ListingCount>
                </PreviewToolbar>

                <StandardModalTableSection>
                    <StandardModalTableScroll style={{ maxHeight: "25rem" }}>
                        <PreviewTable style={{ minWidth: `${1060 + (customFields.length * 160)}px` }}>
                            <thead>
                                <tr>
                                    <th>
                                        <SelectCheckbox
                                            checked={editableRows.length > 0 && selectedIds.length === editableRows.length}
                                            onChange={toggleAll}
                                        />
                                    </th>
                                    <th>Personal Name</th>
                                    <th>Company Name</th>
                                    <th>Title/Position</th>
                                    <th>Department</th>
                                    <th>Email</th>
                                    <th>Phone</th>
                                    <th>Address</th>
                                    <th>Location</th>
                                    <th>Website</th>
                                    <th>Description</th>
                                    {customFields.map(field => <th key={field.uuid}>{field.name}</th>)}
                                    <th>Visible To</th>
                                </tr>
                            </thead>
                            <tbody>
                                {editableRows.map(row => (
                                    <tr key={row.id}>
                                        <td>
                                            <SelectCheckbox
                                                checked={selectedIds.includes(row.id)}
                                                onChange={() => toggleSelected(row.id)}
                                            />
                                        </td>
                                        <td>{input(row, "title", "Personal Name")}</td>
                                        <td>{input(row, "company", "Company Name")}</td>
                                        <td>{input(row, "position", "Title/Position")}</td>
                                        <td>{input(row, "department", "Department")}</td>
                                        <td>{input(row, "email", "Email")}</td>
                                        <td>{input(row, "phoneNumber", "Phone")}</td>
                                        <td>{input(row, "address", "Address")}</td>
                                        <td>{input(row, "country", "Location")}</td>
                                        <td>{input(row, "website", "Website")}</td>
                                        <td>{input(row, "description", "Description")}</td>
                                        {customFields.map(field => (
                                            <td key={field.uuid}>
                                                <StandardModalInput
                                                    aria-label={`${field.name} for row`}
                                                    value={row.customFields[field.uuid] || ""}
                                                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => (
                                                        updateCustomField(row.id, field.uuid, event.target.value)
                                                    )}
                                                />
                                            </td>
                                        ))}
                                        <td>
                                            <StandardModalSelect
                                                aria-label="Visible To for row"
                                                value={row.visibility}
                                                onChange={(event: React.ChangeEvent<HTMLSelectElement>) => (
                                                    updateVisibility(row.id, event.target.value as DirectoryVisibility)
                                                )}
                                            >
                                                <option value="employee">{employeeLabel}</option>
                                                <option value="client">{clientLabel}</option>
                                                <option value="employee_and_client">
                                                    {employeeLabel} & {clientLabel}
                                                </option>
                                            </StandardModalSelect>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </PreviewTable>
                    </StandardModalTableScroll>
                </StandardModalTableSection>

                {errors.map(error => <StandardModalErrorText key={error}>{error}</StandardModalErrorText>)}
            </PreviewLayout>
        </StandardModal>
    );
};

export default DirectoryCsvPreviewModal;
